import React, { useState } from "react";
import styled, { css } from "styled-components";
import { colors } from "../../utils";
import TextView from "../TextView";
import { ASSETS } from "../../utils/config";
import { device } from "../../utils/device";
import SearchIcon from "@mui/icons-material/Search";
import { Close } from "@mui/icons-material";

const InputWrapper = styled.div`
  margin-bottom: 24px;
  position: relative;

  @media (${device.mobilePortrait}) {
    margin-bottom: 16px;
  }
`;

const secondaryStyles = css`
  background-color: white;
  border: 1px solid ${colors.lightBorder};
`;

const successStyles = css`
  border-color: #29c18c;
`;

const errorStyles = css`
  border-color: ${colors.error};
`;

const CustomInput = styled.input`
  width: 100%;
  background-color: ${({ customBgColor }) =>
    customBgColor || colors.background};
  border-radius: 4px;
  height: 36px;
  outline: none;
  border: ${({ customBgColor }) => "1px solid #CED4DA" || "none"};
  padding: 5px 10px;
  margin-top: 5px;
  font-size: 12px;
  position: relative;

  ${({ variant }) => variant === "secondary" && secondaryStyles}

  ${({ isValid }) => isValid === true && successStyles}
  ${({ isValid }) => isValid === false && errorStyles}

  &:focus {
    border: 1px solid ${colors.PrimaryPurple900};
  }

  &:disabled {
    background-color: ${colors.disabled};
    cursor: not-allowed;
  }

  &:-webkit-autofill {
    -webkit-box-shadow: ${({ variant }) =>
      `0 0 0 30px ${
        variant == "secondary" ? colors.white : colors.background
      } inset !important`};
    -webkit-text-fill-color: ${colors.text};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  background-color: ${colors.background};
  border-radius: 4px;
  min-height: 120px;
  outline: none;
  border: none;
  padding: 5px 10px;
  margin-top: 5px;
  font-size: 12px;
  position: relative;
  resize: none;

  ${({ variant }) => variant === "secondary" && secondaryStyles}
  ${({ isValid }) => isValid === true && successStyles}
  ${({ isValid }) => isValid === false && errorStyles}

  &:focus {
    border: 1px solid ${colors.PrimaryPurple900};
  }

  &:disabled {
    background-color: ${colors.lightGrey};
    cursor: not-allowed;
  }
`;

const PasswordIcon = styled.img`
  position: absolute;
  bottom: 8px;
  cursor: pointer;
  opacity: 0.8;
  right: 18px;
`;

const Error = styled(TextView)`
  color: ${colors.error};
  position: absolute;
`;

export default function Input({
  label,
  error,
  type = "input",
  inputStyle,
  labelStyle,
  containerStyle,
  disabled,
  variant,
  isValid,
  value,
  isSearch,
  customBgColor,
  placeholder = isSearch ? "Search" : "",
  showClearIcon,
  onClear,
  ...rest
}) {
  const [showPassword, setShowPassword] = useState(false);
  const isInputValid = value ? isValid : null;

  return (
    <InputWrapper style={containerStyle}>
      {label ? <TextView style={labelStyle}>{label}</TextView> : null}
      {type == "textarea" ? (
        <TextArea
          className={`${disabled && "disabled"}}`}
          value={value}
          disabled={disabled}
          style={inputStyle}
          variant={variant}
          isValid={isInputValid}
          placeholder={placeholder}
          {...rest}
        />
      ) : type == "password" ? (
        <>
          <CustomInput
            value={value}
            className={`${disabled && "disabled"}}`}
            disabled={disabled}
            variant={variant}
            style={{ paddingRight: 45, ...inputStyle }}
            type={showPassword ? "text" : "password"}
            isValid={isInputValid}
            placeholder={placeholder}
            customBgColor={customBgColor}
            {...rest}
          />
          <PasswordIcon
            alt="Toggle password"
            src={showPassword ? ASSETS.peye : ASSETS.peye_off}
            onClick={() => setShowPassword(!showPassword)}
          />
        </>
      ) : (
        <>
          {isSearch && (
            <SearchIcon
              style={{
                position: "absolute",
                zIndex: 100,
                marginTop: 12,
                marginLeft: 8,
                fontSize: 22,
                color: colors.lightGrey,
              }}
            />
          )}
          <CustomInput
            value={value}
            className={`${disabled && "disabled"}}`}
            disabled={disabled}
            variant={variant}
            style={{
              paddingLeft: isSearch ? 32 : 10,
              paddingRight: showClearIcon ? 32 : 10,
              ...inputStyle,
            }}
            isValid={isInputValid}
            placeholder={placeholder}
            {...rest}
          />
          {showClearIcon && (
            <Close
              style={{
                position: "absolute",
                right: 5,
                top: 12,
                color: colors.lightGrey,
                fontSize: 22,
                cursor: "pointer",
              }}
              onClick={onClear}
            />
          )}
        </>
      )}

      {error ? <Error variant="tag">*{error}</Error> : null}
    </InputWrapper>
  );
}
